import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import IgCommentsPanel from "./IgCommentsPanel";
import IgStoryReactionsPanel from "./IgStoryReactionsPanel";

export default function ZernioInstagram({ API }) {
  const [st, setSt] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const pollRef = useRef(null);
  const [contacts, setContacts] = useState(null);
  const [contactSearch, setContactSearch] = useState("");
  const [importing, setImporting] = useState(false);

  const importHistory = async () => {
    setImporting(true);
    try {
      const r = await axios.post(`${API}/zernio/import-history`);
      toast.success(`Импортировано: ${r.data.dialogs} диалогов, ${r.data.messages} сообщений. AI для них выключен 🔴`);
      setContacts(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ошибка импорта истории");
    }
    setImporting(false);
  };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/zernio/instagram/status`);
      setSt(r.data);
      return r.data;
    } catch (e) {
      return null;
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  useEffect(() => {
    function handler(ev) {
      if (ev?.data?.type !== "CHATITY_ZERNIO_IG") return;
      load();
    }
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [load]);

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      const data = await load();
      if (data?.connected || tries > 40) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setConnecting(false);
        if (data?.connected) toast.success(`Instagram @${data.username || ""} подключён — AI отвечает в Direct ✨`);
      }
    }, 3000);
  };

  const connect = async () => {
    try {
      setConnecting(true);
      // open window synchronously (mobile browsers block popups opened after await)
      const win = window.open("", "chatity-zernio-ig", "width=720,height=800,resizable=yes,scrollbars=yes");
      const r = await axios.get(`${API}/zernio/instagram/connect`);
      const url = r.data?.auth_url;
      if (!url) throw new Error("no url");
      if (win && !win.closed) {
        win.location.href = url;
        startPolling();
      } else {
        // popup blocked (mobile) — same-tab redirect; callback will return to dashboard
        window.location.href = url;
      }
    } catch (e) {
      setConnecting(false);
      toast.error(e.response?.data?.detail === "Zernio integration not configured"
        ? "Интеграция ещё не настроена администратором"
        : "Не удалось открыть окно подключения");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Отключить Instagram? AI перестанет отвечать в Direct.")) return;
    try {
      await axios.post(`${API}/zernio/instagram/disconnect`);
      toast.success("Instagram отключён");
      load();
    } catch (e) {
      toast.error("Ошибка отключения");
    }
  };

  const connected = !!st?.connected;

  const loadContacts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/zernio/contacts`);
      setContacts(r.data?.contacts || []);
    } catch (e) { setContacts([]); }
  }, [API]);

  useEffect(() => {
    if (connected && contacts === null) loadContacts();
  }, [connected, contacts, loadContacts]);

  const toggleContactAi = async (c) => {
    const enable = c.ai_disabled;
    try {
      await axios.post(`${API}/inbox/conversations/${encodeURIComponent(c.session_id)}/ai-toggle`, { enabled: enable });
      setContacts((prev) => prev.map((x) => (x.session_id === c.session_id ? { ...x, ai_disabled: !enable } : x)));
      toast.success(enable ? "AI снова отвечает этому клиенту" : "AI выключен для этого клиента");
    } catch (e) { toast.error("Ошибка"); }
  };

  const bulkAi = async (enabled) => {
    try {
      const r = await axios.post(`${API}/zernio/contacts/ai-bulk`, { enabled });
      setContacts((prev) => (prev || []).map((x) => ({ ...x, ai_disabled: !enabled })));
      toast.success(enabled
        ? `AI включён для всех диалогов (${r.data.count})`
        : `AI выключен для всех текущих диалогов (${r.data.count}) — новым клиентам бот продолжит отвечать`);
    } catch (e) { toast.error("Ошибка"); }
  };

  const filteredContacts = useMemo(() => {
    if (!contacts) return [];
    const s = contactSearch.trim().toLowerCase();
    if (!s) return contacts;
    return contacts.filter((c) => (c.name || "").toLowerCase().includes(s) || (c.username || "").toLowerCase().includes(s));
  }, [contacts, contactSearch]);

  return (
    <div className="border-2 border-black rounded-lg p-4 sm:p-6 bg-gradient-to-r from-pink-50 to-orange-50" data-testid="zernio-ig-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <h3 className="text-lg font-bold flex items-center gap-2 flex-wrap">
            <span className="text-2xl">📷</span> Instagram Direct
            {connected ? (
              <Badge className="bg-green-600 text-white" data-testid="zernio-ig-connected-badge">@{st.username || "connected"}</Badge>
            ) : (
              <Badge variant="outline" className="border-gray-400 text-gray-600">Не подключено</Badge>
            )}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            AI отвечает на сообщения в Instagram DM от клиентов. Подключение в пару кликов через вход в Instagram.
          </p>
          {!connected && (
            <>
              <p className="text-xs text-gray-500 mt-2">
                Требование: Instagram-аккаунт должен быть <b>Professional</b> (Business/Creator).
              </p>
              <details className="mt-2 text-xs text-gray-700 bg-white/70 border border-pink-200 rounded-lg p-2" data-testid="zernio-meta-activity-help">
                <summary className="cursor-pointer font-semibold">Ошибка «Будущие действия вашего аккаунта вне платформ Meta выключены»?</summary>
                <p className="mt-2">Это настройка приватности в вашем аккаунте Meta — она блокирует вход во все сторонние сервисы. Включите её обратно (1 минута):</p>
                <ol className="list-decimal ml-4 mt-1 space-y-0.5">
                  <li>Instagram → <b>Настройки</b> → <b>Центр аккаунтов</b> (Accounts Center).</li>
                  <li><b>Ваша информация и разрешения</b> → <b>Ваши действия вне технологий Meta</b>.</li>
                  <li><b>Управление будущими действиями</b> → выберите <b>«Подключить будущие действия»</b> для этого аккаунта.</li>
                  <li>Вернитесь сюда и нажмите «Подключить Instagram» ещё раз. После подключения настройку можно снова отключить — связь с Chatity сохранится.</li>
                </ol>
                <p className="mt-1 text-gray-500">Также убедитесь, что аккаунт Instagram привязан к странице Facebook и вы входите с того же телефона/браузера без блокировщиков cookies.</p>
              </details>
            </>
          )}
          {connected && (
            <div className="mt-4 pt-4 border-t border-pink-200" data-testid="zernio-contacts-panel">
              <h4 className="font-bold text-black text-base">Контакты Instagram</h4>
              <p className="text-xs text-gray-600 mb-2">
                Выберите, с кем AI общается, а с кем нет. Совет: «Отключить AI для всех» — и бот будет отвечать <b>только новым</b> клиентам.
              </p>
              <button type="button" onClick={importHistory} disabled={importing}
                data-testid="zernio-import-history-btn"
                className="mb-3 text-xs font-semibold px-3 py-1.5 rounded-md border-2 border-black bg-white hover:bg-gray-50 disabled:opacity-50">
                {importing ? "⏳ Импортируем..." : "📥 Импортировать историю Direct (до 50 диалогов)"}
              </button>
              <p className="text-[11px] text-gray-500 -mt-2 mb-2">
                Старые диалоги появятся в «Сообщениях» с выключенным AI — бот не будет писать старым контактам сам. Включить AI можно точечно тумблером.
              </p>
              {contacts === null ? (
                <p className="text-sm text-gray-500 py-3">Загружаем диалоги…</p>
              ) : contacts.length === 0 ? (
                <p className="text-xs text-gray-600">Пока нет диалогов — они появятся здесь после первых сообщений клиентов.</p>
              ) : (
                <div className="bg-white border border-gray-200 rounded-md">
                  <div className="flex gap-2 items-center p-2 border-b border-gray-100 flex-wrap">
                    <Input data-testid="zernio-contacts-search" value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      placeholder="Поиск по имени или @username..."
                      className="border-2 border-black h-9 text-sm flex-1 min-w-[160px]" />
                    <button type="button" onClick={() => bulkAi(true)} data-testid="zernio-enable-all-btn"
                      className="text-xs font-semibold underline text-green-700 whitespace-nowrap shrink-0">
                      Включить AI для всех
                    </button>
                    <button type="button" onClick={() => bulkAi(false)} data-testid="zernio-disable-all-btn"
                      className="text-xs font-semibold underline text-red-600 whitespace-nowrap shrink-0">
                      Отключить AI для всех
                    </button>
                  </div>
                  <div className="max-h-72 overflow-y-auto divide-y" data-testid="zernio-contacts-list">
                    {filteredContacts.map((c) => (
                      <div key={c.session_id} className="flex items-center gap-3 px-3 py-2">
                        <span className="flex-1 min-w-0">
                          <span className={`block text-sm font-medium truncate ${c.ai_disabled ? "text-gray-400" : "text-black"}`}>
                            {c.name || (c.username ? `@${c.username}` : "Клиент")}
                          </span>
                          {c.name && c.username && <span className="block text-xs text-gray-500">@{c.username}</span>}
                        </span>
                        <button type="button" onClick={() => toggleContactAi(c)}
                          data-testid={`zernio-ai-toggle-${c.session_id}`}
                          className={`text-xs font-semibold px-3 py-1 rounded-full border shrink-0 transition-colors ${
                            c.ai_disabled
                              ? "border-red-400 text-red-600 bg-red-50 hover:bg-red-100"
                              : "border-green-500 text-green-700 bg-green-50 hover:bg-green-100"
                          }`}>
                          {c.ai_disabled ? "🔴 AI молчит" : "🟢 AI отвечает"}
                        </button>
                      </div>
                    ))}
                    {filteredContacts.length === 0 && (
                      <p className="text-sm text-gray-500 p-4 text-center">Ничего не найдено</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          {connected && <IgCommentsPanel API={API} />}
          {connected && <IgStoryReactionsPanel API={API} />}
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {!connected ? (
            <Button
              data-testid="zernio-ig-connect-btn"
              onClick={connect}
              disabled={connecting}
              className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white"
            >
              {connecting ? "Ожидаем подключения..." : "Подключить Instagram"}
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={disconnect}
              className="border-2 border-red-500 text-red-600 hover:bg-red-50"
              data-testid="zernio-ig-disconnect-btn"
            >
              Отключить
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
